import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { I18nThemeProvider, useI18n } from "@/contexts/I18nContext";
import { BrandingProvider } from "@/contexts/BrandingContext";
import { FeatureVisibilityProvider } from "@/contexts/FeatureVisibilityContext";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import ClientPortalPage from "@/pages/ClientPortalPage";
import UsersManagement from "@/pages/UsersManagement";
import AdminSettingsPage from "@/pages/AdminSettingsPage";
import BrandingPage from "@/pages/BrandingPage";
import SystemHealthPage from "@/pages/SystemHealthPage";
import PricingCatalogPage from "@/pages/PricingCatalogPage";
import PromoCodesPage from "@/pages/PromoCodesPage";
import BonusPolicyPage from "@/pages/BonusPolicyPage";
import MyEarningsPage from "@/pages/MyEarningsPage";
import FinancePage from "@/pages/FinancePage";
import BanksAdminPage from "@/pages/admin/BanksAdminPage";
import ReceiptTemplateAdminPage from "@/pages/admin/ReceiptTemplateAdminPage";
import PerformancePage from "@/pages/admin/PerformancePage";
import DocumentRequirementsAdminPage from "@/pages/admin/DocumentRequirementsAdminPage";
import UserFeaturesPage from "@/pages/admin/UserFeaturesPage";
import AttendanceAdminPage from "@/pages/admin/AttendanceAdminPage";
import PortalCampaignsAdminPage from "@/pages/admin/PortalCampaignsAdminPage";
import AdminInvoiceVerifyPage from "@/pages/admin/AdminInvoiceVerifyPage";
import AdminPoliciesPage from "@/pages/admin/AdminPoliciesPage";
import MuaqqibProfileAdminPage from "@/pages/admin/MuaqqibProfileAdminPage";
import MuaqqibDirectoryAdminPage from "@/pages/admin/MuaqqibDirectoryAdminPage";
import CountriesAdminPage from "@/pages/admin/CountriesAdminPage";
import AttendanceCheckInPage from "@/pages/AttendanceCheckInPage";
import ApplicationsList from "@/pages/workspace/ApplicationsList";
import ApplicationDetail from "@/pages/workspace/ApplicationDetail";
import OfficeSubmit from "@/pages/workspace/OfficeSubmit";
import ApplyRemote from "@/pages/ats/ApplyRemote";
import ApplyTest from "@/pages/ats/ApplyTest";
import AtsDashboard from "@/pages/ats/AtsDashboard";
import AssessmentSheet from "@/pages/ats/AssessmentSheet";
import DemoTry from "@/pages/ats/DemoTry";
import TrainingFlow from "@/pages/ats/TrainingFlow";
import AssociateTasks from "@/pages/associate/AssociateTasks";
import AssociateTaskDetail from "@/pages/associate/AssociateTaskDetail";
import VisaApplicationDemo from "@/pages/associate/VisaApplicationDemo";
import PracticalAssessment from "@/pages/associate/PracticalAssessment";
import PortalHeader from "@/components/PortalHeader";
import PortalFooter from "@/components/PortalFooter";
import AppShell from "@/components/AppShell";
import UpdateGate from "@/components/UpdateGate";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const { t, theme } = useI18n();
  if (loading) {
    return (
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center",
        justifyContent: "center", background: theme.bg, color: theme.textMuted,
        fontFamily: "'Tajawal','Segoe UI', sans-serif",
      }}>
        {t("app.checking_session")}
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // ATS recruiter is a sandboxed account — it sees ONLY /ats/* (no employee
  // dashboard, no client applications, no admin pages, no AppShell sidebar).
  const roleCode = ((user.role || {}).code || user.role_code || "").toLowerCase();
  if (roleCode === "ats_recruiter") {
    const path = window.location.pathname;
    if (!path.startsWith("/ats")) return <Navigate to="/ats/dashboard" replace />;
    // Render the recruiter page WITHOUT the employee AppShell.
    return <>{children}</>;
  }
  // Data entry associate is the most isolated role — sees ONLY /associate/*.
  // No AppShell, no office branding, no admin/finance/applications routes.
  if (roleCode === "data_entry_associate") {
    const path = window.location.pathname;
    if (!path.startsWith("/associate")) return <Navigate to="/associate/tasks" replace />;
    return <>{children}</>;
  }
  return <AppShell>{children}</AppShell>;
}

function PortalLayout() {
  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <PortalHeader />
      <main style={{ flex: 1 }}><ClientPortalPage /></main>
      <PortalFooter />
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <BrandingProvider>
          <I18nThemeProvider>
            <UpdateGate>
              <Routes>
              {/* Public portal — decoupled from internal Auth, accessible via /portal OR /apply */}
              <Route path="/portal/*" element={<PortalLayout />} />
              {/* ATS Recruitment Portal — public candidate flow (anonymous: no
                  office name, no logo, no domain hint).  The neutral path is
                  the one we advertise; the longer one is kept as an alias. */}
              <Route path="/assessment/visa-operations" element={<ApplyRemote />} />
              <Route path="/apply/remote-visa-operations" element={<ApplyRemote />} />
              {/* Public, obfuscated entry — used in paid ads / social posts.
                  No auth gate, no internal redirects, decoupled from any
                  branding so stale caches can't bounce users to /login. */}
              <Route path="/r/:tok" element={<ApplyRemote />} />
              <Route path="/career/:tok" element={<ApplyRemote />} />
              <Route path="/demo-try" element={<DemoTry />} />
              <Route path="/apply/test/:sid" element={<ApplyTest />} />
              <Route path="/assessment/test/:sid" element={<ApplyTest />} />
              <Route path="/apply/training/:token" element={<TrainingFlow />} />
              <Route path="/assessment/training/:token" element={<TrainingFlow />} />
              <Route path="/apply/*" element={<PortalLayout />} />
              <Route
                path="/*"
                element={
                  <AuthProvider>
                    <Routes>
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/" element={<Protected><DashboardPage /></Protected>} />
                      <Route path="/applications" element={<Protected><ApplicationsList /></Protected>} />
                      <Route path="/applications/new" element={<Protected><OfficeSubmit /></Protected>} />
                      {/* ATS recruiter dashboard */}
                      <Route path="/ats/dashboard" element={<Protected><AtsDashboard /></Protected>} />
                      <Route path="/ats/sheet/:aid" element={<Protected><AssessmentSheet /></Protected>} />
                      <Route path="/ats" element={<Navigate to="/ats/dashboard" replace />} />
                      {/* Data Entry Associate — sandboxed routes */}
                      <Route path="/associate" element={<Navigate to="/associate/tasks" replace />} />
                      <Route path="/associate/tasks" element={<Protected><AssociateTasks /></Protected>} />
                      <Route path="/associate/tasks/:id" element={<Protected><AssociateTaskDetail /></Protected>} />
                      <Route path="/associate/training" element={<Protected><VisaApplicationDemo /></Protected>} />
                      <Route path="/associate/demo" element={<Protected><VisaApplicationDemo /></Protected>} />
                      <Route path="/associate/assessment" element={<Protected><PracticalAssessment /></Protected>} />
                      <Route path="/applications/:id" element={<Protected><ApplicationDetail /></Protected>} />
                      <Route path="/admin/users" element={<Protected><UsersManagement /></Protected>} />
                      <Route path="/admin/settings" element={<Protected><AdminSettingsPage /></Protected>} />
                      <Route path="/admin/branding" element={<Protected><BrandingPage /></Protected>} />
                      <Route path="/admin/system-health" element={<Protected><SystemHealthPage /></Protected>} />
                      <Route path="/admin/pricing-catalog" element={<Protected><PricingCatalogPage /></Protected>} />
                      <Route path="/admin/promo-codes" element={<Protected><PromoCodesPage /></Protected>} />
                      <Route path="/admin/bonus-policy" element={<Protected><BonusPolicyPage /></Protected>} />
                      <Route path="/my-earnings" element={<Protected><MyEarningsPage /></Protected>} />
                      <Route path="/finance" element={<Protected><FinancePage /></Protected>} />
                      <Route path="/admin/banks" element={<Protected><BanksAdminPage /></Protected>} />
                      <Route path="/admin/receipt-template" element={<Protected><ReceiptTemplateAdminPage /></Protected>} />
                      <Route path="/admin/performance" element={<Protected><PerformancePage /></Protected>} />
                      <Route path="/admin/document-requirements" element={<Protected><DocumentRequirementsAdminPage /></Protected>} />
                      <Route path="/admin/users/:userId/features" element={<Protected><UserFeaturesPage /></Protected>} />
                      <Route path="/admin/attendance" element={<Protected><AttendanceAdminPage /></Protected>} />
                      <Route path="/admin/portal-campaigns" element={<Protected><PortalCampaignsAdminPage /></Protected>} />
                      <Route path="/admin/invoice-verify" element={<Protected><AdminInvoiceVerifyPage /></Protected>} />
                      <Route path="/admin/policies" element={<Protected><AdminPoliciesPage /></Protected>} />
                      <Route path="/admin/muaqqib-profile" element={<Protected><MuaqqibProfileAdminPage /></Protected>} />
                      <Route path="/admin/muaqqibs" element={<Protected><MuaqqibDirectoryAdminPage /></Protected>} />
                      <Route path="/admin/countries" element={<Protected><CountriesAdminPage /></Protected>} />
                      <Route path="/attendance" element={<Protected><AttendanceCheckInPage /></Protected>} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </AuthProvider>
                }
              />
              </Routes>
            </UpdateGate>
          </I18nThemeProvider>
        </BrandingProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
